import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
// settings-*/ai-tools-* 클래스 스타일 SSOT(8443 SettingsPage.css 와 동일) — admin 이식 시 누락분 복원.
import './SettingsSections.css';
// settings-* 를 svc 톤으로 재스킨(미러 무수정, .ai-svctone 스코프).
import './AiSvcTone.css';
// 카테고리 구분·상태 hero·애니메이션 — 신규 기능 격리 스타일.
import './AiProviderEnhance.css';
import {
  detectAiProviders,
  listAiProviderModels,
  setAiProviderSelection,
  checkAiProviderHealth,
  type AiProviderDetectResult,
  type AiProviderModel,
  type AiProviderHealthResult,
  type EnvProvider,
  type CliProvider,
} from '../../../api/aiProviders';
import {
  tSettings,
  getSettingsLang,
  subscribeSettingsLang,
  type SettingsI18nKey,
} from '../../../lib/settings-i18n';
import { ProviderIcon } from './AiProviderIcons';
import { StyledSelect } from './MenuControls';
import { ManualConnectModal } from './ManualConnectModal';
import { AiOfficeProviderPanel } from './AiOfficeProviderPanel';
import { ConsoleScreenHead } from '../../../components/console-list/ConsoleScreenHead';

// ─── 인라인 SVG 아이콘 (이모지 미사용) ───────────────────────────
const IconKey = ({ size = 17 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="7.5" cy="15.5" r="4.5" /><path d="M10.7 12.3 21 2M16 7l3 3M14 9l2.5 2.5" />
  </svg>
);
const IconTerminal = ({ size = 17 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="4" width="18" height="16" rx="2" /><path d="m7 9 3 3-3 3M13 15h4" />
  </svg>
);
const IconSpark = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" />
  </svg>
);

// detect 응답에 selection 이 누락된 경우(백엔드 일시 degraded·빈 200 패스스루 등) 화이트스크린
// 방지용 기본값 — selection 부재 시 "미선택/default" 상태로 graceful 렌더 (기존 동작 보존, additive).
const EMPTY_SELECTION = {
  selected_id: null as string | null,
  model_id: null as string | null,
  persisted: false,
  scope: 'default' as 'user' | 'global' | 'default',
  updated_at: null as string | null,
};

/**
 * AiProviderSection — Settings 의 AI Provider 탭.
 *
 * 간소화 디자인 — 카드 1줄 layout:
 *   [큰 아이콘 48px]  AI 이름   [모델 dropdown ▼]   [활성화 / 비활성화]
 *
 * 권한: ADMIN/SYSTEM_ADMIN 만 (SettingsPage 가 tab 노출 제어).
 * 데이터:
 *   GET /api/v1/admin/ai-providers/detect
 *   GET /api/v1/admin/ai-providers/models?providerId=...
 *   PUT /api/v1/admin/ai-providers/selection  body: {selected_id, model_id}
 */
export function AiProviderSection() {
  useSyncExternalStore(subscribeSettingsLang, getSettingsLang, getSettingsLang);
  const [data, setData] = useState<AiProviderDetectResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // 수동 연결 모달 + detect 재조회 트리거.
  const [showManual, setShowManual] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  // provider 별 model list (lazy load) — providerId 키
  const [modelsByProvider, setModelsByProvider] = useState<
    Record<string, AiProviderModel[] | 'loading' | 'unsupported' | 'error'>
  >({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    detectAiProviders()
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  // 활성 provider 의 model list 자동 로드
  useEffect(() => {
    const sel = data?.selection?.selected_id;
    if (!sel) return;
    void loadModels(sel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.selection?.selected_id]);

  const loadModels = useCallback(async (providerId: string) => {
    setModelsByProvider((m) => ({ ...m, [providerId]: 'loading' }));
    try {
      const r = await listAiProviderModels(providerId);
      if (r.source === 'unsupported') {
        setModelsByProvider((m) => ({ ...m, [providerId]: 'unsupported' }));
      } else {
        setModelsByProvider((m) => ({ ...m, [providerId]: r.models }));
      }
    } catch {
      setModelsByProvider((m) => ({ ...m, [providerId]: 'error' }));
    }
  }, []);

  const handleActivate = useCallback(
    async (providerId: string, modelId: string | null) => {
      if (!data) return;
      const prevSelection = data.selection ?? EMPTY_SELECTION;
      if (
        prevSelection.selected_id === providerId &&
        (prevSelection.model_id ?? null) === modelId
      ) {
        return;
      }
      setSavingId(providerId);
      setSaveError(null);
      setData((d) =>
        d
          ? {
              ...d,
              selection: {
                ...d.selection,
                selected_id: providerId,
                model_id: modelId,
                persisted: true,
                scope: 'user',
                updated_at: new Date().toISOString(),
              },
            }
          : d,
      );
      try {
        const res = await setAiProviderSelection(providerId, modelId);
        setData((d) =>
          d
            ? {
                ...d,
                selection: {
                  ...d.selection,
                  selected_id: res.selected_id,
                  model_id: res.model_id,
                  persisted: true,
                  scope: 'user',
                  updated_at: res.updated_at,
                },
              }
            : d,
        );
      } catch (e) {
        setData((d) => (d ? { ...d, selection: prevSelection } : d));
        setSaveError(e instanceof Error ? e.message : String(e));
      } finally {
        setSavingId(null);
      }
    },
    [data],
  );

  // 비활성화 = user-scope selection 삭제 → global/default fallback 회귀
  const handleDeactivate = useCallback(async () => {
    if (!data) return;
    const prevSelection = data.selection ?? EMPTY_SELECTION;
    setSavingId('__deactivate__');
    setSaveError(null);
    setData((d) =>
      d
        ? {
            ...d,
            selection: {
              ...d.selection,
              selected_id: null,
              model_id: null,
              persisted: false,
              scope: 'default',
              updated_at: null,
            },
          }
        : d,
    );
    try {
      await setAiProviderSelection(null, null);
    } catch (e) {
      setData((d) => (d ? { ...d, selection: prevSelection } : d));
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingId(null);
    }
  }, [data]);

  if (loading) {
    return (
      <section className="settings-section">
        <p className="settings-muted">{tSettings('settings.aiProvider.loading')}</p>
      </section>
    );
  }
  if (error || !data) {
    return (
      <section className="settings-section">
        <p className="settings-error">
          {tSettings('settings.aiProvider.error.load')}
          {error && <span> ({error})</span>}
        </p>
      </section>
    );
  }

  const selection = data.selection ?? EMPTY_SELECTION;
  const providers = data.providers ?? { env: [], cli: [] };
  const selectedId = selection.selected_id;
  const activeModel = selection.model_id;
  const activeProvider =
    [...providers.env, ...providers.cli].find((p) => p.id === selectedId) ?? null;
  const scopeKey: SettingsI18nKey =
    selection.scope === 'user'
      ? 'settings.aiProvider.scope.user'
      : selection.scope === 'global'
        ? 'settings.aiProvider.scope.global'
        : 'settings.aiProvider.scope.default';

  return (
    <section className="settings-section settings-ai-provider ait-prov ai-svctone">
      <ConsoleScreenHead
        title={tSettings('settings.aiProvider.heading')}
        desc={tSettings('settings.aiProvider.subtitle')}
      />
      {saveError && <p className="settings-error">{tSettings('settings.aiProvider.error.save')}</p>}

      {/* 상태 hero — 현재 활성 AI */}
      <div className="ait-prov-hero" data-active={activeProvider ? 'true' : 'false'}>
        <span className="ait-prov-hero__icon">
          {activeProvider
            ? <ProviderIcon vendor={activeProvider.vendor} size={28} />
            : <IconSpark size={24} />}
        </span>
        <div className="ait-prov-hero__body">
          <span className="ait-prov-hero__label">{tSettings('settings.aiProvider.hero.active')}</span>
          {activeProvider ? (
            <span className="ait-prov-hero__name">{activeProvider.name}</span>
          ) : (
            <span className="ait-prov-hero__name ait-prov-hero__name--none">
              {tSettings('settings.aiProvider.hero.none')}
            </span>
          )}
          <div className="ait-prov-hero__meta">
            <span className="ait-prov-hero__chip">scope: {tSettings(scopeKey)}</span>
            {activeModel && (
              <span className="ait-prov-hero__chip">
                {tSettings('settings.aiProvider.activeModel')}: <code>{activeModel}</code>
              </span>
            )}
          </div>
        </div>
        {activeProvider && <span className="ait-prov-hero__pulse" aria-hidden />}
      </div>

      {/* 수동 연결 진입 — 외부 API LLM(Claude/OpenAI/Gemini/Local 등) */}
      <button type="button" className="aipm-add" onClick={() => setShowManual(true)}>
        <span className="aipm-add__icon">
          <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M12 5v14M5 12h14" />
          </svg>
        </span>
        <span>
          <span className="aipm-add__title">외부 API LLM 수동 연결</span>
          <span className="aipm-add__desc">Claude · OpenAI/Codex · Gemini · Local LLM 을 직접 입력해 연결하고 테스트합니다.</span>
        </span>
      </button>

      {/* 카테고리: API 키 연동(env) */}
      <div className="ait-prov-cat ait-prov-cat--env">
        <header className="ait-prov-cat__head">
          <span className="ait-prov-cat__icon"><IconKey /></span>
          <span className="ait-prov-cat__title">{tSettings('settings.aiProvider.section.env')}</span>
          <span className="ait-prov-cat__count">{providers.env.length}</span>
          <span className="ait-prov-cat__desc">{tSettings('settings.aiProvider.section.env.desc')}</span>
        </header>
        {providers.env.length === 0 ? (
          <p className="settings-muted">{tSettings('settings.aiProvider.empty.env')}</p>
        ) : (
          <ul className="settings-ai-provider-list">
            {providers.env.map((p) => (
              <ProviderCard
                key={p.id}
                provider={p}
                vendor={p.vendor}
                active={p.id === selectedId}
                activeModel={p.id === selectedId ? activeModel : null}
                models={modelsByProvider[p.id] ?? null}
                busy={savingId === p.id || savingId === '__deactivate__'}
                onLoadModels={() => loadModels(p.id)}
                onActivate={(modelId) => handleActivate(p.id, modelId)}
                onDeactivate={handleDeactivate}
              />
            ))}
          </ul>
        )}
      </div>

      {/* 카테고리: CLI 연동 */}
      <div className="ait-prov-cat ait-prov-cat--cli">
        <header className="ait-prov-cat__head">
          <span className="ait-prov-cat__icon"><IconTerminal /></span>
          <span className="ait-prov-cat__title">{tSettings('settings.aiProvider.section.cli')}</span>
          <span className="ait-prov-cat__count">{providers.cli.length}</span>
          <span className="ait-prov-cat__desc">{tSettings('settings.aiProvider.section.cli.desc')}</span>
        </header>
        {providers.cli.length === 0 ? (
          <p className="settings-muted">{tSettings('settings.aiProvider.empty.cli')}</p>
        ) : (
          <ul className="settings-ai-provider-list">
            {providers.cli.map((p) => (
              <ProviderCard
                key={p.id}
                provider={p}
                vendor={p.vendor}
                active={p.id === selectedId}
                activeModel={p.id === selectedId ? activeModel : null}
                models={modelsByProvider[p.id] ?? null}
                busy={savingId === p.id || savingId === '__deactivate__'}
                onLoadModels={() => loadModels(p.id)}
                onActivate={(modelId) => handleActivate(p.id, modelId)}
                onDeactivate={handleDeactivate}
              />
            ))}
          </ul>
        )}
      </div>

      {/*
        오피스 편집기 전용 provider (2026-08-15, PR4) — AI Provider **하위** 블록.
        위 목록이 이미 감지한 provider 를 그대로 넘겨 중복 왕복을 만들지 않는다.
      */}
      <AiOfficeProviderPanel providers={providers} globalSelectedId={selectedId} />

      {showManual && (
        <ManualConnectModal
          onClose={() => setShowManual(false)}
          onSaved={() => setReloadTick((t) => t + 1)}
        />
      )}
    </section>
  );
}

// ─── unified card (env + cli) ────────────────────────────────────────────

interface ProviderCardProps {
  provider: EnvProvider | CliProvider;
  vendor: string;
  active: boolean;
  activeModel: string | null;
  models: AiProviderModel[] | 'loading' | 'unsupported' | 'error' | null;
  busy: boolean;
  onLoadModels: () => void;
  onActivate: (modelId: string | null) => void;
  onDeactivate: () => void;
}

function ProviderCard({
  provider,
  vendor,
  active,
  activeModel,
  models,
  busy,
  onLoadModels,
  onActivate,
  onDeactivate,
}: ProviderCardProps) {
  const [selectedModel, setSelectedModel] = useState<string>(activeModel ?? '');
  // 헬스체크 — 카드별 독립 상태.
  const [health, setHealth] = useState<'idle' | 'checking' | AiProviderHealthResult>('idle');

  const runHealthCheck = useCallback(async () => {
    setHealth('checking');
    try {
      const r = await checkAiProviderHealth(provider.id);
      setHealth(r);
    } catch (e) {
      setHealth({ ok: false, latency_ms: 0, detail: e instanceof Error ? e.message : String(e) });
    }
  }, [provider.id]);

  useEffect(() => {
    setSelectedModel(activeModel ?? '');
  }, [activeModel]);

  useEffect(() => {
    if (models === null) onLoadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modelOptions = Array.isArray(models) ? models : null;
  const modelsLoading = models === 'loading';
  const modelsError = models === 'error';
  const modelsUnsupported = models === 'unsupported';

  return (
    <li className={`settings-ai-provider-card${active ? ' settings-ai-provider-card--active' : ''}`}>
      <div className="settings-ai-provider-card-icon" aria-hidden>
        <ProviderIcon vendor={vendor} size={36} />
      </div>

      <div className="settings-ai-provider-card-name">{provider.name}</div>

      <div className="settings-ai-provider-card-modelarea">
        {modelsLoading && (
          <span className="settings-muted">{tSettings('settings.aiProvider.model.loading')}</span>
        )}
        {modelsError && (
          <span className="settings-error">{tSettings('settings.aiProvider.model.error')}</span>
        )}
        {modelsUnsupported && (
          <span className="settings-muted">{tSettings('settings.aiProvider.model.unsupported')}</span>
        )}
        {modelOptions && modelOptions.length === 0 && (
          <span className="settings-muted">{tSettings('settings.aiProvider.model.empty')}</span>
        )}
        {modelOptions && modelOptions.length > 0 && (
          <div className="settings-ai-provider-select-wrap">
            <StyledSelect
              value={selectedModel}
              disabled={busy}
              portal
              onChange={setSelectedModel}
              placeholder={tSettings('settings.aiProvider.field.model')}
              options={[
                { value: '', label: tSettings('settings.aiProvider.model.useDefault') },
                ...modelOptions.map((m) => ({ value: m.id, label: m.name })),
              ]}
            />
          </div>
        )}
      </div>

      <div className="settings-ai-provider-card-action">
        {/* 헬스체크 — 활성 provider 일 때 노출(연결 동작 확인). */}
        {active && (
          <div className="settings-ai-provider-health">
            <button
              type="button"
              className="settings-ai-provider-card-btn settings-ai-provider-card-btn--health"
              onClick={() => void runHealthCheck()}
              disabled={busy || health === 'checking'}
            >
              {health === 'checking'
                ? tSettings('settings.aiProvider.health.checking')
                : tSettings('settings.aiProvider.btn.healthCheck')}
            </button>
            {health !== 'idle' && health !== 'checking' && (
              <span
                className={`settings-ai-provider-health-result settings-ai-provider-health-result--${health.ok ? 'ok' : 'fail'}`}
                title={health.detail ?? ''}
                role="status"
              >
                <span className="settings-ai-provider-health-dot" aria-hidden />
                {health.ok
                  ? `${tSettings('settings.aiProvider.health.ok')} (${health.latency_ms}ms)`
                  : tSettings('settings.aiProvider.health.fail')}
              </span>
            )}
          </div>
        )}
        {active ? (
          <button
            type="button"
            className="settings-ai-provider-card-btn settings-ai-provider-card-btn--deactivate"
            onClick={onDeactivate}
            disabled={busy}
          >
            {busy
              ? tSettings('settings.aiProvider.saving')
              : tSettings('settings.aiProvider.btn.deactivate')}
          </button>
        ) : (
          <button
            type="button"
            className="settings-ai-provider-card-btn settings-ai-provider-card-btn--activate"
            onClick={() => onActivate(selectedModel || null)}
            disabled={busy}
          >
            {busy
              ? tSettings('settings.aiProvider.saving')
              : tSettings('settings.aiProvider.btn.activate')}
          </button>
        )}
      </div>
    </li>
  );
}
